import React from 'react';
import Layout from '../components/Layout';
import Card from '../components/Card';
import { Heart, Target, Eye, Users, MapPin, Clock, Shield, Zap, Star } from 'react-feather';

const About = () => {
  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <Card className="text-center">
          <div className="py-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-4">About TenaRx</h1>
            <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
              TenaRx is a digital healthcare platform designed to improve access to essential medicines 
              by connecting patients with pharmacies in real time.
            </p>
          </div>
        </Card>

        {/* Problem Statement */}
        <Card>
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center">
              <MapPin className="mr-2 text-blue-600" />
              The Challenge
            </h2>
            <p className="text-gray-600 leading-relaxed">
              In many regions, including Addis Ababa, individuals often face significant challenges 
              when trying to locate necessary medications. The process can involve visiting multiple 
              pharmacies, dealing with uncertain availability, and experiencing critical delays—especially 
              in urgent situations.
            </p>
            <p className="text-gray-600 leading-relaxed">
              TenaRx addresses this problem by providing a reliable, efficient, and user-friendly 
              solution that simplifies how people find and access medicines.
            </p>
          </div>
        </Card>

        {/* Mission & Vision */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                <Target className="mr-2 text-green-600" />
                Our Mission
              </h2>
              <p className="text-gray-600 leading-relaxed">
                To ensure that everyone can access the medicines they need quickly, reliably, 
                and without unnecessary stress through the use of technology.
              </p>
            </div>
          </Card>
          
          <Card>
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                <Eye className="mr-2 text-purple-600" />
                Our Vision
              </h2>
              <p className="text-gray-600 leading-relaxed">
                To become a leading healthcare access platform across Africa, transforming the 
                way patients, pharmacies, and suppliers connect and interact.
              </p>
            </div>
          </Card>
        </div>

        {/* What We Offer */}
        <Card>
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center">
              <Users className="mr-2 text-blue-600" />
              What We Offer
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-700">For Customers:</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <Clock className="mr-2 text-blue-500 mt-1 flex-shrink-0" size={16} />
                    <span>Real-time search for medicines</span>
                  </li>
                  <li className="flex items-start">
                    <MapPin className="mr-2 text-blue-500 mt-1 flex-shrink-0" size={16} />
                    <span>Access to nearby pharmacies with available stock</span>
                  </li>
                  <li className="flex items-start">
                    <Zap className="mr-2 text-blue-500 mt-1 flex-shrink-0" size={16} />
                    <span>Smart recommendations for alternative medications when needed</span>
                  </li>
                  <li className="flex items-start">
                    <Shield className="mr-2 text-blue-500 mt-1 flex-shrink-0" size={16} />
                    <span>Notifications when previously unavailable medicines are restocked</span>
                  </li>
                </ul>
              </div>
              
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-700">For Pharmacies:</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <Users className="mr-2 text-green-500 mt-1 flex-shrink-0" size={16} />
                    <span>A system to list and manage available medicines</span>
                  </li>
                  <li className="flex items-start">
                    <Clock className="mr-2 text-green-500 mt-1 flex-shrink-0" size={16} />
                    <span>Real-time inventory updates</span>
                  </li>
                  <li className="flex items-start">
                    <MapPin className="mr-2 text-green-500 mt-1 flex-shrink-0" size={16} />
                    <span>Direct connection with customers</span>
                  </li>
                  <li className="flex items-start">
                    <Target className="mr-2 text-green-500 mt-1 flex-shrink-0" size={16} />
                    <span>Increased visibility and reach within their local market</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </Card>

        {/* What Sets Us Apart */}
        <Card>
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center">
              <Star className="mr-2 text-yellow-600" />
              What Sets Us Apart
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start space-x-3">
                <Clock className="text-blue-500 mt-1 flex-shrink-0" size={20} />
                <div>
                  <h3 className="font-medium text-gray-700">Real-time Availability</h3>
                  <p className="text-sm text-gray-600">Reducing uncertainty and delays</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Shield className="text-green-500 mt-1 flex-shrink-0" size={20} />
                <div>
                  <h3 className="font-medium text-gray-700">Verified Pharmacy Network</h3>
                  <p className="text-sm text-gray-600">Ensuring trust and reliability</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Zap className="text-purple-500 mt-1 flex-shrink-0" size={20} />
                <div>
                  <h3 className="font-medium text-gray-700">Intelligent Alternative Suggestions</h3>
                  <p className="text-sm text-gray-600">Helping users make informed decisions</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <MapPin className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                <div>
                  <h3 className="font-medium text-gray-700">Location-based Services</h3>
                  <p className="text-sm text-gray-600">Enabling fast and convenient access</p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Our Story */}
        <Card>
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center">
              <Heart className="mr-2 text-red-600" />
              Our Story
            </h2>
            <p className="text-gray-600 leading-relaxed">
              TenaRx was founded on a clear and urgent need to improve access to essential medicines.
            </p>
            <p className="text-gray-600 leading-relaxed">
              The idea emerged from a personal experience while searching for critical medication 
              for a severely ill family member. Despite significant effort, locating the required 
              medicine proved to be time-consuming and uncertain, highlighting inefficiencies in 
              accessing basic healthcare resources.
            </p>
            <p className="text-gray-600 leading-relaxed">
              During this time, another incident left a lasting impression—an elderly woman in 
              visible distress, struggling to find an inhaler and fearing for her life due to the delay.
            </p>
            <p className="text-gray-600 leading-relaxed">
              These experiences underscored a broader and recurring challenge faced by many individuals 
              across Ethiopia, where access to timely and reliable information about medicine availability 
              remains limited.
            </p>
            <p className="text-gray-600 leading-relaxed">
              In response, TenaRx was created to bridge this gap by providing a platform that 
              connects patients with pharmacies in real time, ensuring faster and more reliable 
              access to essential medications.
            </p>
          </div>
        </Card>

        {/* Join Us */}
        <Card className="bg-gradient-to-r from-green-50 to-emerald-50">
          <div className="text-center space-y-4 py-6">
            <h2 className="text-2xl font-bold text-gray-800">Join Us</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              TenaRx is committed to improving healthcare accessibility through innovation and collaboration.
            </p>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Whether you are a patient seeking timely access to medicine or a pharmacy aiming to 
              better serve your community, TenaRx provides the tools to make that connection 
              seamless and effective.
            </p>
            <div className="pt-4">
              <p className="text-lg font-semibold text-green-600">
                TenaRx — Simplifying Access to Essential Medicines
              </p>
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default About;
